import { useState } from 'react';
import { TEAM_PROFILES, type TeamProfile } from '../constants/teamProfiles';
import { BASELINE_PROJECTIONS } from '../constants/teamProjections';
import { HISTORIC_2025_SALES } from '../constants/season2025Recap';
import { TEAMS_BY_ABBR } from '../constants/teams';

export interface PriorSaleHistory {
  year: number;
  price: number;
  winner: string;
  winnerId?: string;
}

interface Props {
  abbr: string;
  pastSales?: PriorSaleHistory[];
  compact?: boolean;
}

export function TeamScoutingCard({ abbr, pastSales, compact = false }: Props) {
  const [expanded, setExpanded] = useState(!compact);
  const profile: TeamProfile | undefined = TEAM_PROFILES[abbr];
  const projection = BASELINE_PROJECTIONS[abbr];
  const meta = TEAMS_BY_ABBR[abbr];

  // Resolve prior year sales: combine passed sales with 2025 historic archive
  const salesMap: Record<number, PriorSaleHistory> = {};
  if (HISTORIC_2025_SALES[abbr]) {
    salesMap[2025] = {
      year: 2025,
      price: HISTORIC_2025_SALES[abbr].price,
      winner: HISTORIC_2025_SALES[abbr].winner,
      winnerId: HISTORIC_2025_SALES[abbr].winnerId,
    };
  }
  if (pastSales) {
    for (const s of pastSales) {
      salesMap[s.year] = s;
    }
  }
  const salesList = Object.values(salesMap).sort((a, b) => b.year - a.year);

  return (
    <div className="bg-slate-900/90 rounded-xl border border-cyan-500/30 overflow-hidden shadow-lg mb-3">
      {/* Top Banner: Prior Year Sales */}
      <div className="bg-slate-950/90 px-4 py-2 border-b border-white/5 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-amber-400 font-extrabold text-[11px] uppercase tracking-wider">
            📜 Prior Auction History:
          </span>
          {salesList.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {salesList.map(sale => (
                <span
                  key={sale.year}
                  className="px-2 py-0.5 rounded-md bg-amber-950/80 border border-amber-500/40 text-amber-300 font-semibold text-[11px] flex items-center gap-1 shadow-sm"
                >
                  <span className="text-slate-400 font-normal">{sale.year}:</span>
                  <span className="text-emerald-400 font-extrabold">${sale.price}</span>
                  <span className="text-slate-300 font-normal">to</span>
                  <span className="text-white font-bold">{sale.winner}</span>
                </span>
              ))}
            </div>
          ) : (
            <span className="text-slate-500 italic text-[11px]">No prior year draft history</span>
          )}
        </div>

        <button
          onClick={() => setExpanded(e => !e)}
          className="text-[11px] text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1 font-semibold"
        >
          <span>{expanded ? 'Hide Intel ▲' : '2026 Scouting Intel ▼'}</span>
        </button>
      </div>

      {/* Main Scouting Dossier */}
      {expanded && profile && (
        <div className="p-3.5 space-y-3 text-xs">
          {/* Quick Stat Tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="bg-black/30 p-2 rounded-lg border border-white/5 text-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold block">Last Season Record</span>
              <span className="text-sm font-extrabold text-amber-300">{profile.lastSeasonRecord}</span>
              <span className="text-[9px] text-slate-500 block truncate">{profile.lastSeasonFinish.split('·')[0]}</span>
            </div>
            <div className="bg-black/30 p-2 rounded-lg border border-white/5 text-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold block">2026 Projected Wins</span>
              <span className="text-sm font-extrabold text-cyan-300">{projection?.projectedWins.toFixed(1) ?? '8.5'}W</span>
              <span className="text-[9px] text-slate-500 block">FPI Rank #{projection?.fpiRank ?? 16}</span>
            </div>
            <div className="bg-black/30 p-2 rounded-lg border border-white/5 text-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold block">Schedule Rank</span>
              <span className="text-sm font-extrabold text-teal-300">#{projection?.sosRank ?? 16} SOS</span>
              <span className="text-[9px] text-slate-500 block">Bye Wk {projection?.byeWeek ?? 9}</span>
            </div>
            <div className="bg-black/30 p-2 rounded-lg border border-white/5 text-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold block">Coaching Staff</span>
              <span className="text-xs font-extrabold text-white truncate block">{profile.headCoach.split('(')[0]}</span>
              <span className="text-[9px] text-slate-500 block truncate">{meta?.conference} {meta?.division}</span>
            </div>
          </div>

          {/* 2026 Expectations */}
          <div className="bg-cyan-950/30 border border-cyan-500/20 p-2.5 rounded-lg">
            <div className="text-[10px] uppercase font-extrabold text-cyan-400 mb-0.5">
              🎯 2026 Season Expectations & Outlook:
            </div>
            <p className="text-slate-200 text-xs leading-relaxed">
              {profile.expectations2026}
            </p>
          </div>

          {/* Big Offseason Player Movements & Trades */}
          <div className="bg-black/25 p-2.5 rounded-lg border border-white/5">
            <div className="text-[10px] uppercase font-extrabold text-teal-300 mb-1.5 flex items-center gap-1">
              <span>🔄</span>
              <span>Big Offseason Movements & Roster Changes:</span>
            </div>
            <ul className="space-y-1">
              {profile.bigPlayerMovements.map((move, i) => (
                <li key={i} className="flex items-start gap-1.5 text-slate-300 text-[11px] leading-tight">
                  <span className="text-teal-400 font-bold">•</span>
                  <span>{move}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Key Playmakers & Scheme */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
            <div className="bg-black/20 p-2 rounded border border-white/5">
              <span className="text-slate-400 font-bold block text-[10px] uppercase">Offensive Scheme</span>
              <p className="text-slate-300 mt-0.5">{profile.offensiveOutlook}</p>
            </div>
            <div className="bg-black/20 p-2 rounded border border-white/5">
              <span className="text-slate-400 font-bold block text-[10px] uppercase">Defensive Scheme</span>
              <p className="text-slate-300 mt-0.5">{profile.defensiveOutlook}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
